import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db2026 } from "../config/firebase";
import { assertRoundWritable, getActiveDb } from "../config/tournamentRuntime";
import { auditService } from "./auditService";

const getDb = () => getActiveDb() || db2026;

// Collection names
const SCORES_COLLECTION = "scores";
const TEAMS_COLLECTION = "teams";

// Score document structure:
// {
//   id: "teamId-hole-round", // e.g., "1-5-1" for team 1, hole 5, round 1
//   teamId: 1,
//   hole: 5,
//   round: 1,
//   strokes: 4,
//   updatedBy: "user@email.com",
//   updatedAt: timestamp
// }

export const scoresService = {
  // Save a score for a specific team, hole, and round
  async saveScore(teamId, hole, round, strokes, userEmail) {
    try {
      assertRoundWritable(round);
      const scoreId = `${teamId}-${hole}-${round}`;
      const scoreRef = doc(getDb(), SCORES_COLLECTION, scoreId);

      // Check if score already exists (for audit logging)
      const existingScore = await getDoc(scoreRef);
      const oldScore = existingScore.exists()
        ? existingScore.data().strokes
        : null;

      const scoreData = {
        id: scoreId,
        teamId: parseInt(teamId),
        hole: parseInt(hole),
        round: parseInt(round),
        strokes: parseInt(strokes),
        updatedBy: userEmail,
        updatedAt: new Date(),
      };

      // Save the score
      await setDoc(scoreRef, scoreData);

      // Log the audit entry
      if (oldScore !== null) {
        // This is an update
        await auditService.logScoreUpdate(scoreData, userEmail, oldScore);
        console.log(
          `📝 Audit: ${userEmail} updated Team ${teamId} Hole ${hole} Round ${round}: ${oldScore} → ${strokes}`
        );
      } else {
        // This is a new score
        await auditService.logScoreCreate(scoreData, userEmail);
        console.log(
          `📝 Audit: ${userEmail} created Team ${teamId} Hole ${hole} Round ${round}: ${strokes}`
        );
      }

      return scoreData;
    } catch (error) {
      console.error("Error saving score:", error);
      throw error;
    }
  },

  // Get a specific score
  async getScore(teamId, hole, round) {
    try {
      const scoreId = `${teamId}-${hole}-${round}`;
      const scoreRef = doc(getDb(), SCORES_COLLECTION, scoreId);
      const scoreDoc = await getDoc(scoreRef);

      if (scoreDoc.exists()) {
        return scoreDoc.data();
      }
      return null;
    } catch (error) {
      console.error("Error getting score:", error);
      throw error;
    }
  },

  // Get all scores for a team
  async getTeamScores(teamId) {
    try {
      const scoresQuery = query(
        collection(getDb(), SCORES_COLLECTION),
        where("teamId", "==", parseInt(teamId)),
        orderBy("round"),
        orderBy("hole")
      );

      const querySnapshot = await getDocs(scoresQuery);
      const scores = {};

      querySnapshot.forEach((doc) => {
        const scoreData = doc.data();
        const key = `${scoreData.teamId}-${scoreData.hole}-${scoreData.round}`;
        scores[key] = scoreData.strokes;
      });

      return scores;
    } catch (error) {
      console.error("Error getting team scores:", error);
      throw error;
    }
  },

  // Get all scores for all teams
  async getAllScores() {
    try {
      const scoresQuery = query(
        collection(getDb(), SCORES_COLLECTION),
        orderBy("teamId"),
        orderBy("round"),
        orderBy("hole")
      );

      const querySnapshot = await getDocs(scoresQuery);
      const scores = {};

      querySnapshot.forEach((doc) => {
        const scoreData = doc.data();
        const key = `${scoreData.teamId}-${scoreData.hole}-${scoreData.round}`;
        scores[key] = scoreData.strokes;
      });

      return scores;
    } catch (error) {
      console.error("Error getting all scores:", error);
      throw error;
    }
  },

  // Real-time listener for all scores (no orderBy — avoids index failures)
  onScoresChange(callback, errorCallback) {
    const scoresCol = collection(getDb(), SCORES_COLLECTION);

    return onSnapshot(
      scoresCol,
      (querySnapshot) => {
        const scores = {};
        querySnapshot.forEach((docSnap) => {
          const scoreData = docSnap.data() || {};
          const key =
            scoreData.id ||
            `${scoreData.teamId}-${scoreData.hole}-${scoreData.round}`;
          if (scoreData.strokes == null || scoreData.strokes === "") {
            return;
          }
          const strokes = Number(scoreData.strokes);
          if (Number.isFinite(strokes)) {
            scores[key] = strokes;
          }
        });
        callback(scores);
      },
      (error) => {
        console.error("Firestore listener error:", error);
        if (errorCallback) {
          errorCallback(error);
        }
      }
    );
  },

  // Real-time listener for a specific team's scores
  onTeamScoresChange(teamId, callback) {
    const scoresQuery = query(
      collection(getDb(), SCORES_COLLECTION),
      where("teamId", "==", parseInt(teamId))
    );

    return onSnapshot(scoresQuery, (querySnapshot) => {
      const scores = {};
      querySnapshot.forEach((docSnap) => {
        const scoreData = docSnap.data() || {};
        const key =
          scoreData.id ||
          `${scoreData.teamId}-${scoreData.hole}-${scoreData.round}`;
        if (scoreData.strokes == null || scoreData.strokes === "") {
          return;
        }
        const strokes = Number(scoreData.strokes);
        if (Number.isFinite(strokes)) {
          scores[key] = strokes;
        }
      });
      callback(scores);
    });
  },

  // Calculate team totals
  calculateTeamTotal(teamId, round, scores) {
    let total = 0;
    let holesPlayed = 0;

    for (let hole = 1; hole <= 18; hole++) {
      const key = `${teamId}-${hole}-${round}`;
      if (scores[key] == null || scores[key] === "") {
        continue;
      }
      const strokes = Number(scores[key]);
      if (Number.isFinite(strokes)) {
        total += strokes;
        holesPlayed++;
      }
    }

    return { total, holesPlayed };
  },

  // Calculate cumulative totals for both rounds
  calculateCumulativeTotal(teamId, scores) {
    const round1 = this.calculateTeamTotal(teamId, 1, scores);
    const round2 = this.calculateTeamTotal(teamId, 2, scores);

    return {
      round1: round1.total,
      round2: round2.total,
      total: round1.total + round2.total,
      holesPlayed: round1.holesPlayed + round2.holesPlayed,
    };
  },
};
